import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, CircularProgress, Alert, TextField, Select, MenuItem, Button } from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { paymentService } from '../../services/paymentService';
import { getApiError } from '../../utils/apiError';

interface StripeCCFieldProps {
  questionnaireToken: string;
  onPaymentReady: (ready: boolean) => void;
  onPaymentSuccess?: () => void;
  /** Label for the pay button (amount is prefixed automatically). Defaults to "Book Appointment". */
  buttonLabel?: string;
}

export interface StripeCCFieldHandle {
  confirmPayment: () => Promise<{ success: boolean; error?: string }>;
}

// Inner component that uses Stripe hooks
const PaymentForm = forwardRef<
  StripeCCFieldHandle,
  {
    onPaymentReady: (ready: boolean) => void;
    onError: (error: string | null) => void;
    amount: number;
    clientSecret: string;
    onPaymentSuccess?: () => void;
    buttonLabel?: string;
  }
>(({ onPaymentReady, onError, amount, clientSecret, onPaymentSuccess, buttonLabel }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('GB');
  const [cardBrand, setCardBrand] = useState<string | null>(null);

  // Common field styling
  const fieldStyle = {
    base: {
      fontSize: '16px',
      color: '#3A7380',
      fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
    },
  };

  useEffect(() => {
    // Notify parent when Stripe is ready
    if (stripe && elements) {
      onPaymentReady(true);
    }
  }, [stripe, elements, onPaymentReady]);

  // Shared payment handler function
  const handlePayment = async (): Promise<{ success: boolean; error?: string }> => {
      if (!stripe || !elements) {
        const errorMsg = 'Stripe not loaded';
        onError(errorMsg);
        return { success: false, error: errorMsg };
      }

      setIsProcessing(true);
      onError(null); // Clear any previous errors

      try {
        // Validate postal code
        if (!postalCode || postalCode.trim().length === 0) {
          const errorMsg = 'Please enter your postal code';
          onError(errorMsg);
          setIsProcessing(false);
          return { success: false, error: errorMsg };
        }

        // Get CardNumber element to create payment method
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          const errorMsg = 'Card details not loaded';
          onError(errorMsg);
          setIsProcessing(false);
          return { success: false, error: errorMsg };
        }

        // Create payment method with card details
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            address: {
              postal_code: postalCode,
              country: country,
            },
          },
        });

        if (paymentMethodError) {
          const errorMsg = paymentMethodError.message || 'Invalid card details';
          onError(errorMsg);
          setIsProcessing(false);
          return { success: false, error: errorMsg };
        }

        // Confirm the payment
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id,
          }
        );

        setIsProcessing(false);

        if (confirmError) {
          const errorMsg = confirmError.message || 'Payment failed';
          onError(errorMsg);
          return {
            success: false,
            error: errorMsg,
          };
        }

        onError(null);

        // Call success callback if provided
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }

        return { success: true };
      } catch (err: any) {
        setIsProcessing(false);
        const errorMsg = err.message || 'Payment failed';
        onError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }
  };

  // Expose confirmPayment method to parent
  useImperativeHandle(ref, () => ({
    confirmPayment: handlePayment,
  }));

  // Format amount for display
  const displayAmount = (amount / 100).toFixed(2);

  return (
    <Box>
      {/* Card Header with Icon - 45% width */}
      <div className="flex items-center justify-between rounded-lg px-4 py-4 mb-3 border border-gray-200 w-[45%]">
        <span className="text-md font-medium text-[#3A7380]">Card</span>
        <svg width="24" height="23" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="31" fill="url(#pattern0_634_983)"/>
          <defs>
            <pattern id="pattern0_634_983" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_634_983" transform="matrix(0.0078125 0 0 0.00806452 0 -0.016129)"/>
            </pattern>
            <image id="image0_634_983" width="128" height="128" preserveAspectRatio="none" xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAACXBIWXMAAAOwAAADsAEnxA+tAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAABShJREFUeJztnT1vHUUUhp97zYdRAlIaCiIoQEg0dFAQAwppiWVFyp9AFHyDUCJBFIIiQPT0fLQkpk1IERIKfgCgCBwkwKYh6EYCg2JTbK6xnYt9Zu+cmb33vI90JBfrOaM9752d2d13B4QQQgghhBBCCCFECHqV8s4AB4AFYA54ENgH3F6pP5POP8DvwA/AReAMcBm4UbNTo7gLeB1YAdYVrrECvAbMmipTgKPAT9Q/MdHiKnDEUB83esDbwBr1T0bUWANOA/2dS5WfPvDZGB1X5I1P2DbvmxlRtJy8AzznnEPYeZRGABdKJDuKhv0uxhqb5gRey8BZ4Hvgfqf2xXj8DDwM/Ok1KXgBFb/L7AeeB58RYAb4BbjXoW2RjxXgPo+GnyLtmnQWOAjs8ehMEPYAzwCLpJ37OY/OfJDQgTc9OhCcY9jP/3seHbhsTH7WI7kA7CPBVx7JfzMmP+iRXABwCFsNlj2SrxqT7/VILgC4G1sN/vJIPjAk/sMjsdiCaR7gcR/gm0zHiAllnt2V92y13sXBuhJw4dQOCU96JRVbqCoAgMPAeZo5wQA4h375JTEJoNY7gcIf06+7+BsioltIAMGRAIIjAQRHAgiOBBAcCSA4EkBwJIDg3GY8Tm7evMjNq7glcrt5x34YJDdvncjl5m0tALl560cON28rAcjN2624xc2bQCsB7PQih6JOnNheJCPJApCbt5uxxc2bgKn94fAiN2+32XDzJvzP+u6H/DfJkJu322y4eXPTQ27eSWHo5l0zHm8aAUBu3hqUcPNa25SbtzJebl6zAOTmrY+Hm9csALl56+Ph5jULQG7e+ni4eU0C6N38w4JMJL7kroOpPb0QEhwJIDgSQHAkgOB4CmCB5qPE17EvSSJHNTw6eDqhXYWPUEzteSwDF4DPEzoq0uj8MvAlhzaFEx4jwADdNfSk8yOAmCBqfSdQdAQPAXzo0KZwwkMAi8C7Du0KJ7xuVGz+TmDttfM0hZVq9wFEO3LXQasAsTsSQHAkgOBIAMGRAKaTe4zHrUoA08ljxuOuSQDTycvG465IANPHcewbc1wCvzuBIo1x6rCXxl30RUI768AB3QnsDqV/ZMvAfl0C4vI+N781oEtANyj5QOlH4M7UxMKXUsVfo3lxNzmx8KWUAN5qm1j4UqL4HzNiMi8BdAPvYf9/Pz0rAXQDr+Ivse2a3zax8CV34X8FXmHTbH8U1g0jRLdZBa4BV2g+JHUG+BrjNwWjjQCeruXrwJfAfIt+VatDJAGUdC2fSuybBODMAuWKP4zDCf2TAJy5QHkBnE/oX5U6RHoaWMO1PMD+elaVOuhpoC+dHzkjCaCGa3kinNJR5gDzlJ8DWF/NIqHN7EQRAJTdFOtkYt8kgEJ4upYHwDnSfvlDqtQh0iqg61SpQ6RJoBiBBBAcCSA4EkBw+sDfxmP18Uc/zG7e3In7NC8SWHg8d3KxgdnN65H8Erb156JHcgHYPX0XPZKnbBx5zKMDwTmO/fynbBxp5smEDgxHgkNoTjAOrd28uTuizaMnh2WaXcStm0eb6AM3aC4DottsuHlzMryvPAt8BzyQO4HIwhLwCE7LQGi2JH2R6XrqNy2s09Qme/Ghuf4P+ZbGKPK0RyLRmhPAR6WS9YBPyf+cXNEuRrp5vekBb9BMDmufgKixo5u3FEeAq9Q/GdFiiV3cvCWZBV6lWYPWPjHTHiY3b26s15c+8ASNMueAh4B9wB1O/Zp2xnLzCiGEEEIIIYQQQgiRzL9iZ1H84RY/2QAAAABJRU5ErkJggg=="/>
          </defs>
        </svg>
      </div>

      {/* All Payment Fields Container */}
      <Box
        sx={{
          border: '1px solid #f0f0f0',
          borderRadius: { xs: '10px', md: '20px' },
          padding: 3,
          backgroundColor: 'white',
          mb: 3,
        }}
      >
        {/* Card Number */}
        <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: '#3A7380',
          }}
        >
          Card Number
        </Typography>
        <Box
          sx={{
            position: 'relative',
            border: '1px solid #e0e0e0',
            borderRadius: { xs: '10px', md: '20px' },
            padding: '12px 14px',
            backgroundColor: 'white',
            '&:hover': {
              borderColor: '#a3a3a3',
            },
            '&:focus-within': {
              borderColor: '#635bff',
              boxShadow: '0 0 0 1px #635bff',
            },
          }}
        >
          <CardNumberElement
            options={{
              style: fieldStyle,
              showIcon: false,
              disableLink: true,
            }}
            onChange={(event) => {
              if (event.brand) {
                setCardBrand(event.brand);
              } else {
                setCardBrand(null);
              }
            }}
          />

          {/* Card Brand Icons */}
          <Box
            sx={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: { xs: 'none', md: 'flex' },  // Hide on mobile
              gap: '6px',
              alignItems: 'center',
            }}
          >
            {/* Visa */}
            <Box
              component="img"
              src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg"
              sx={{
                height: '20px',
                opacity: !cardBrand || cardBrand === 'visa' ? 1 : 0.3,
                transition: 'opacity 0.2s',
              }}
            />
            {/* Mastercard */}
            <Box
              component="img"
              src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg"
              sx={{
                height: '20px',
                opacity: !cardBrand || cardBrand === 'mastercard' ? 1 : 0.3,
                transition: 'opacity 0.2s',
              }}
            />
            {/* Amex */}
            <Box
              component="img"
              src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg"
              sx={{
                height: '20px',
                opacity: !cardBrand || cardBrand === 'amex' ? 1 : 0.3,
                transition: 'opacity 0.2s',
              }}
            />
            {/* Discover */}
            <Box
              component="img"
              src="https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg"
              sx={{
                height: '20px',
                opacity: !cardBrand || cardBrand === 'discover' ? 1 : 0.3,
                transition: 'opacity 0.2s',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Expiry Date and Security Code Row */}
      <Box sx={{ display: 'flex', gap: '12px', mb: 2 }}>
        {/* Expiry Date */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: '#3A7380',
            }}
          >
            Expiry Date
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: { xs: '10px', md: '20px' },
              padding: '12px 14px',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#a3a3a3',
              },
              '&:focus-within': {
                borderColor: '#635bff',
                boxShadow: '0 0 0 1px #635bff',
              },
            }}
          >
            <CardExpiryElement options={{ style: fieldStyle }} />
          </Box>
        </Box>

        {/* Security Code */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: '#3A7380',
            }}
          >
            Security Code
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: { xs: '10px', md: '20px' },
              padding: '12px 14px',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#a3a3a3',
              },
              '&:focus-within': {
                borderColor: '#635bff',
                boxShadow: '0 0 0 1px #635bff',
              },
            }}
          >
            <CardCvcElement options={{ style: fieldStyle }} />
          </Box>
        </Box>
      </Box>

      {/* Country + Postal Code Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: '12px',
          mt: 2,
        }}
      >
        {/* Country Field */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: '#3A7380',
            }}
          >
            Country
          </Typography>
          <Select
            fullWidth
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '& .MuiSelect-select': {
                padding: '12px 14px',
                fontSize: '16px',
                backgroundColor: 'white',
              },
              borderRadius: { xs: '10px', md: '20px' },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#a3a3a3',
              },
            }}
          >
            <MenuItem value="GB">United Kingdom</MenuItem>
            <MenuItem value="US">United States</MenuItem>
            <MenuItem value="CA">Canada</MenuItem>
            <MenuItem value="AU">Australia</MenuItem>
          </Select>
        </Box>

        {/* Postal Code Field */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: '#3A7380',
            }}
          >
            Postal code
          </Typography>
          <TextField
            fullWidth
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
            variant="outlined"
            inputProps={{
              maxLength: 8,
              style: {
                padding: '12px 14px',
                fontSize: '16px',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: { xs: '10px', md: '20px' },
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#a3a3a3',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#635bff',
                  borderWidth: '1px',
                },
              },
            }}
          />
        </Box>
      </Box>
      </Box>

      {/* Payment Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={async () => {
            await handlePayment();
            // Success navigation is handled by onPaymentSuccess callback
          }}
          disabled={isProcessing || !stripe || !elements}
          variant="contained"
          sx={{
            backgroundColor: '#3A7380',
            color: 'white',
            padding: '10px 20px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: { xs: '10px', md: '20px' },
            minWidth: '200px',
            height: '40px',
            '&:hover': {
              backgroundColor: '#2e5c66',
            },
            '&.Mui-disabled': {
              backgroundColor: '#B0B0B0',
              color: 'white',
            },
          }}
        >
          {isProcessing ? 'Processing...' : `£${displayAmount} ${buttonLabel || 'Book Appointment'}`}
        </Button>
      </Box>

      {isProcessing && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Processing payment...</Typography>
        </Box>
      )}
    </Box>
  );
});

PaymentForm.displayName = 'PaymentForm';

// Main component
const StripeCCField = forwardRef<StripeCCFieldHandle, StripeCCFieldProps>(
  ({ questionnaireToken, onPaymentReady, onPaymentSuccess, buttonLabel }, ref) => {
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const paymentFormRef = React.useRef<StripeCCFieldHandle>(null);

    // Forward confirmPayment to the inner PaymentForm
    useImperativeHandle(ref, () => ({
      confirmPayment: async () => {
        if (paymentFormRef.current) {
          return paymentFormRef.current.confirmPayment();
        }
        return { success: false, error: 'Payment form not ready' };
      },
    }));

    useEffect(() => {
      // Load Stripe configuration and create payment intent
      const initializePayment = async () => {
        try {
          setLoading(true);
          setError(null);

          // Get Stripe publishable key
          const config = await paymentService.getConfig();
          // Pin the locale so Stripe error/decline messages (e.g. "Your card
          // was declined") come back in English instead of the browser language.
          const stripeInstance = loadStripe(config.publishable_key, { locale: 'en-GB' });
          setStripePromise(stripeInstance);

          // Create payment intent
          const paymentIntent = await paymentService.createIntent(questionnaireToken);
          setClientSecret(paymentIntent.client_secret);
          setAmount(paymentIntent.amount);

          setLoading(false);
        } catch (err: any) {
          console.error('Error initializing payment:', err);
          setError(getApiError(err, 'Error loading payment system'));
          setLoading(false);
          onPaymentReady(false);
        }
      };

      initializePayment();
    }, [questionnaireToken, onPaymentReady]);

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} sx={{ mr: 2 }} />
          <Typography>Loading payment system...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!clientSecret || !stripePromise) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          Error loading payment system
        </Alert>
      );
    }

    return (
      <Box sx={{ my: 2 }}>
        <Box sx={{ position: 'relative' }}>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#1976d2',
                },
              },
              locale: 'en-GB',
            }}
          >
            <PaymentForm
              ref={paymentFormRef}
              onPaymentReady={onPaymentReady}
              onError={setPaymentError}
              amount={amount}
              clientSecret={clientSecret}
              onPaymentSuccess={onPaymentSuccess}
              buttonLabel={buttonLabel}
            />
          </Elements>
        </Box>

        {paymentError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {paymentError}
          </Alert>
        )}
      </Box>
    );
  }
);

StripeCCField.displayName = 'StripeCCField';

export default StripeCCField;
